/**
 * RevenueCat Service — modular, fail-safe wrapper for iOS IAP
 *
 * CRITICAL FIX: Capacitor's registerPlugin() returns a Proxy that
 * intercepts ALL property accesses — including `.then()`. If any
 * async function or Promise chain resolves with this proxy object,
 * JS Promise resolution calls `.then()` on it, which Capacitor
 * interprets as a native method call → UNIMPLEMENTED error.
 *
 * Solution: `ensurePlugin()` resolves to a boolean, never the proxy.
 * All callers access the module-level `_plugin` variable directly.
 */

import { Capacitor } from "@capacitor/core";
import { supabase } from "@/integrations/supabase/client";
import { logger } from "@/lib/logger";

// ── Constants ──────────────────────────────────────────────
export const ENTITLEMENT_ID = "pro";

export const REVENUECAT_PRODUCT_IDS = {
  starter: "tm_starter_monthly",
  growth: "tm_growth_monthly",
  pro: "tm_pro_monthly",
} as const;

export const PRODUCT_TO_TIER: Record<string, string> = {
  tm_starter_monthly: "starter",
  tm_growth_monthly: "growth",
  tm_pro_monthly: "pro",
};

// ── Types ──────────────────────────────────────────────────
export interface RCIntroOffer {
  /** e.g. "2 weeks", "1 month" — raw from StoreKit */
  periodLabel: string;
  /** Number of units (e.g. 2 for "2 weeks") */
  periodUnits: number;
  /** Unit type: "day" | "week" | "month" | "year" */
  periodUnit: string;
  /** Price during intro (usually 0 for free trial) */
  price: number;
  priceString: string;
}

export type IntroEligibility = "eligible" | "ineligible" | "unknown";

export interface RCPackage {
  identifier: string;
  productId: string;
  localizedTitle: string;
  localizedDescription: string;
  priceString: string;
  price: number;
  hasIntroOffer: boolean;
  /** Parsed intro offer details from StoreKit product data */
  introOffer: RCIntroOffer | null;
  /** Eligibility for the intro offer — set after checkIntroEligibility */
  introEligibility: IntroEligibility;
  _raw: any;
}

export interface RCSubscriptionStatus {
  isActive: boolean;
  productId: string | null;
  expirationDate: string | null;
  willRenew: boolean;
  isSandbox: boolean;
  periodType: string | null;
}

// ── Plugin singleton ───────────────────────────────────────
// _plugin holds the Capacitor Proxy — NEVER return it from a
// Promise/.then() chain or async function.
let _plugin: any = null;
let _initialized = false;
let _initPromise: Promise<boolean> | null = null;
let _nativeBridgeAvailable = true;
let _pluginLoading: Promise<boolean> | null = null;
let _configuredAppUserId: string | null = null;

/**
 * ensurePlugin — loads the plugin module and stores the proxy
 * in `_plugin`. Resolves to `true` if plugin is ready, `false`
 * otherwise. NEVER resolves with the proxy object itself.
 */
async function ensurePlugin(): Promise<boolean> {
  if (!Capacitor.isNativePlatform()) return false;
  if (_plugin) return true;
  if (_pluginLoading) return _pluginLoading;

  _pluginLoading = (async () => {
    try {
      logger.debug("[RC] Importing plugin…");
      const mod = await import("@revenuecat/purchases-capacitor");
      _plugin = mod.Purchases;
      logger.debug("[RC] Plugin loaded");
      return true;
    } catch (e: any) {
      logger.error("[RC] Plugin load failed:", e?.code, e?.message);
      return false;
    } finally {
      _pluginLoading = null;
    }
  })();

  return _pluginLoading;
}

/**
 * safeCall — wraps any async plugin call, catching UNIMPLEMENTED
 * and any other error. Returns fallback on failure.
 */
async function safeCall<T>(
  fn: () => Promise<T>,
  fallback: T,
  label: string
): Promise<{ value: T; ok: boolean; cancelled?: boolean }> {
  if (!_nativeBridgeAvailable) {
    logger.warn(`[RC] ${label}: skipping — native bridge unavailable`);
    return { value: fallback, ok: false };
  }
  try {
    logger.debug(`[RC] >>> ${label}`);
    const result = await fn();
    logger.debug(`[RC] <<< ${label}: OK`);
    return { value: result, ok: true };
  } catch (e: any) {
    const code = String(e?.code || "");
    const msg = e?.message || String(e);

    // Detect user cancellation (RC error code 1 or "PURCHASE_CANCELLED")
    if (
      code === "1" ||
      code === "PURCHASE_CANCELLED" ||
      msg.includes("PURCHASE_CANCELLED") ||
      msg.includes("Purchase was cancelled")
    ) {
      logger.debug(`[RC] ${label}: user cancelled`);
      return { value: fallback, ok: false, cancelled: true };
    }

    // Log as warning for expected UNIMPLEMENTED, error for others
    if (
      code === "UNIMPLEMENTED" ||
      msg.includes("UNIMPLEMENTED") ||
      msg.includes("not implemented") ||
      msg.includes("is not available") ||
      msg.includes("Web not supported")
    ) {
      logger.warn(`[RC] ${label}: UNIMPLEMENTED — disabling native bridge`);
      _nativeBridgeAvailable = false;
    } else {
      logger.error(`[RC] ${label}: FAILED — ${code}`);
    }
    return { value: fallback, ok: false };
  }
}

// ── Global unhandled rejection catcher for Capacitor bridge ──
if (typeof window !== "undefined") {
  window.addEventListener("unhandledrejection", (event) => {
    const reason = event?.reason;
    const code = reason?.code || "";
    const msg = reason?.message || String(reason || "");
    if (
      code === "UNIMPLEMENTED" ||
      msg?.includes?.("UNIMPLEMENTED") ||
      msg?.includes?.("not implemented") ||
      msg?.includes?.("Purchases") ||
      msg?.includes?.("Web not supported")
    ) {
      logger.debug("[RC] Suppressed unhandled rejection:", code);
      event.preventDefault();
    }
  });
}

// ── Public helpers ─────────────────────────────────────────
export function isIOSNative(): boolean {
  try {
    return Capacitor.isNativePlatform() && Capacitor.getPlatform() === "ios";
  } catch {
    return false;
  }
}

export function isNativeBridgeAvailable(): boolean {
  return _nativeBridgeAvailable;
}

// ── Init (idempotent, deduped) ─────────────────────────────
export async function initRevenueCat(): Promise<boolean> {
  const {
    data: { user },
  } = await supabase.auth.getUser();
  const currentUserId = user?.id ?? null;

  logger.debug("[RC] initRevenueCat — init:", _initialized, "bridge:", _nativeBridgeAvailable);

  if (!isIOSNative()) return false;
  if (!_nativeBridgeAvailable) {
    logger.warn("[RC] initRevenueCat: native bridge unavailable");
    return false;
  }

  const pluginReady = await ensurePlugin();
  if (!pluginReady || !_plugin) {
    logger.error("[RC] initRevenueCat: no plugin loaded");
    return false;
  }

  if (_initialized) {
    if (_configuredAppUserId === currentUserId) {
      return true;
    }

    logger.debug("[RC] initRevenueCat: identity change detected");

    const switchResult = currentUserId
      ? await safeCall(
          () => _plugin.logIn({ appUserID: currentUserId }),
          undefined,
          "logIn(identity-switch)"
        )
      : await safeCall(() => _plugin.logOut(), undefined, "logOut(identity-switch)");

    if (switchResult.ok) {
      _configuredAppUserId = currentUserId;
      if (currentUserId) {
        return true;
      }
      _initialized = false;
    } else {
      logger.warn("[RC] initRevenueCat: identity switch failed, re-configuring");
      _initialized = false;
      _configuredAppUserId = null;
    }
  }

  if (_initPromise) return _initPromise;

  _initPromise = (async () => {
    try {
      const apiKey = "appl_ltTYcRKNcVOCSIRoeMeIvoUJQFA";

      logger.debug("[RC] configure() starting");
      const configResult = await safeCall(
        () => _plugin.configure({ apiKey, appUserID: currentUserId }),
        undefined,
        "configure"
      );

      if (!configResult.ok) {
        logger.error("[RC] configure() FAILED");
        return false;
      }

      if (import.meta.env.DEV) {
        await safeCall(
          () => _plugin.setLogLevel({ level: "DEBUG" }),
          undefined,
          "setLogLevel"
        );
      }

      _initialized = true;
      _configuredAppUserId = currentUserId;
      logger.debug("[RC] Initialized successfully");
      return true;
    } catch (e: any) {
      logger.error("[RC] initRevenueCat: unexpected error:", e?.message);
      return false;
    } finally {
      _initPromise = null;
    }
  })();

  return _initPromise;
}

// ── Login / Logout ─────────────────────────────────────────
export interface RevenueCatIdentity {
  email?: string | null;
  displayName?: string | null;
}

/**
 * Sets the RevenueCat subscriber attributes so the RevenueCat dashboard shows
 * who a customer is instead of only an internal user ID. Best-effort only —
 * never blocks login or purchase flows.
 */
export async function setRevenueCatIdentityAttributes(
  identity: RevenueCatIdentity
): Promise<void> {
  await ensurePlugin();
  if (!_plugin || !_initialized) return;

  if (identity.email) {
    await safeCall(() => _plugin.setEmail({ email: identity.email! }), undefined, "setEmail");
  }
  if (identity.displayName) {
    await safeCall(
      () => _plugin.setDisplayName({ displayName: identity.displayName! }),
      undefined,
      "setDisplayName"
    );
  }
}

export async function loginRevenueCat(
  userId: string,
  identity?: RevenueCatIdentity
): Promise<void> {
  const initOk = await initRevenueCat();
  await ensurePlugin();
  if (!_plugin) return;
  if (_configuredAppUserId === userId) {
    if (identity) await setRevenueCatIdentityAttributes(identity);
    return;
  }
  if (!initOk && !_initialized) return;

  const loginResult = await safeCall(
    () => _plugin.logIn({ appUserID: userId }),
    undefined,
    "logIn"
  );

  if (loginResult.ok) {
    _initialized = true;
    _configuredAppUserId = userId;
    if (identity) await setRevenueCatIdentityAttributes(identity);
  }
}

export async function logoutRevenueCat(): Promise<void> {
  await ensurePlugin();
  if (_plugin && _initialized) {
    await safeCall(() => _plugin.logOut(), undefined, "logOut");
  }
  _configuredAppUserId = null;
  _initialized = false;
  _initPromise = null;
}

// ── Safe Customer Info Fetch ──────────────────────────────
export async function getCustomerInfoSafe(): Promise<any | null> {
  logger.debug("[RC] getCustomerInfoSafe");
  await ensurePlugin();
  if (!_plugin || !_initialized) {
    logger.debug("[RC] getCustomerInfoSafe: not ready");
    return null;
  }
  const result = await safeCall(
    async () => {
      const response = await _plugin.getCustomerInfo();
      return response?.customerInfo ?? null;
    },
    null,
    "getCustomerInfo"
  );
  return result.value;
}

// ── Deep customerInfo diagnostic logger (dev only) ────────
function logCustomerInfoDeep(label: string, customerInfo: any) {
  if (!customerInfo) {
    logger.debug(`[RC] ${label}: customerInfo is null`);
    return;
  }
  try {
    const entitlements = customerInfo.entitlements;
    const activeKeys = Object.keys(entitlements?.active || {});
    logger.debug(`[RC] ${label}: active entitlements:`, activeKeys,
      "activeSubs:", customerInfo.activeSubscriptions);
  } catch (e: any) {
    logger.debug(`[RC] ${label}: logging error:`, e?.message);
  }
}

// ── Entitlement check (pure, no async) ────────────────────
export function hasActiveEntitlementFromInfo(
  customerInfo: any | null,
  entitlementId: string = ENTITLEMENT_ID
): boolean {
  if (!customerInfo) return false;

  // Primary check: entitlements.active[id]
  const has = !!customerInfo?.entitlements?.active?.[entitlementId];

  // Fallback: check if ANY active subscription exists (covers RC dashboard misconfiguration)
  if (!has) {
    const activeSubs = customerInfo?.activeSubscriptions;
    const knownProducts: string[] = Object.values(REVENUECAT_PRODUCT_IDS);
    const hasActiveSub = Array.isArray(activeSubs) && activeSubs.some((s: string) => knownProducts.includes(s));
    if (hasActiveSub) {
      logger.debug("[RC] Granting via activeSubscriptions fallback");
      return true;
    }
  }

  return has;
}

// ── Legacy wrapper (kept for backward compat) ─────────────
export async function hasActiveEntitlement(): Promise<boolean> {
  const info = await getCustomerInfoSafe();
  return hasActiveEntitlementFromInfo(info);
}

export async function getSubscriptionStatus(): Promise<RCSubscriptionStatus> {
  const empty: RCSubscriptionStatus = {
    isActive: false,
    productId: null,
    expirationDate: null,
    willRenew: false,
    isSandbox: false,
    periodType: null,
  };
  const info = await getCustomerInfoSafe();
  if (!info) return empty;

  const ent = info?.entitlements?.active?.[ENTITLEMENT_ID];
  if (!ent) return empty;

  return {
    isActive: true,
    productId: ent.productIdentifier || null,
    expirationDate: ent.expirationDate || null,
    willRenew: ent.willRenew ?? true,
    isSandbox: ent.isSandbox ?? false,
    periodType: (ent as any).periodType || null,
  };
}

// ── Offerings ──────────────────────────────────────────────
export async function getOfferings(): Promise<RCPackage[]> {
  logger.debug("[RC] getOfferings");
  await ensurePlugin();
  if (!_plugin || !_initialized) {
    logger.warn("[RC] getOfferings: not ready");
    return [];
  }

  const result = await safeCall(
    () => _plugin.getOfferings(),
    null,
    "getOfferings"
  );

  if (!result.ok || !result.value) {
    logger.warn("[RC] getOfferings: call failed");
    return [];
  }

  try {
    const offerings = result.value;
    logger.debug("[RC] Raw offerings received");
    const current = offerings?.current;
    if (!current?.availablePackages?.length) {
      logger.warn("[RC] getOfferings: no current offering or no packages");
      return [];
    }

    return current.availablePackages.map((pkg: any) => {
      const product = pkg.product || {};
      const introPrice = product.introPrice || product.introductoryPrice;
      const hasIntro =
        !!introPrice ||
        !!product.freeTrialPeriod;

      // Parse intro offer details
      let introOffer: RCIntroOffer | null = null;
      if (introPrice) {
        const unit = introPrice.periodUnit || introPrice.subscriptionPeriod?.unit || "unknown";
        const units = introPrice.periodNumberOfUnits || introPrice.subscriptionPeriod?.numberOfUnits || introPrice.cycles || 1;
        const unitLabel = unit === "week" ? "weeks" : unit === "day" ? "days" : unit === "month" ? "months" : unit === "year" ? "years" : unit;
        introOffer = {
          periodLabel: `${units} ${unitLabel}`,
          periodUnits: units,
          periodUnit: unit,
          price: introPrice.price ?? 0,
          priceString: introPrice.priceString || "Free",
        };
      } else if (product.freeTrialPeriod) {
        introOffer = {
          periodLabel: product.freeTrialPeriod,
          periodUnits: 0,
          periodUnit: "unknown",
          price: 0,
          priceString: "Free",
        };
      }

      return {
        identifier: pkg.identifier,
        productId: product.identifier || pkg.identifier,
        localizedTitle: product.title || pkg.identifier,
        localizedDescription: product.description || "",
        priceString: product.priceString || "",
        price: product.price || 0,
        hasIntroOffer: hasIntro,
        introOffer,
        introEligibility: "unknown" as IntroEligibility,
        _raw: pkg,
      };
    });
  } catch (e: any) {
    logger.error("[RC] getOfferings parse error:", e?.message);
    return [];
  }
}

// ── Intro Eligibility Check ────────────────────────────────
export async function checkIntroEligibility(
  packages: RCPackage[]
): Promise<RCPackage[]> {
  await ensurePlugin();
  if (!_plugin || !_initialized) {
    logger.debug("[RC] checkIntroEligibility: not ready");
    return packages;
  }

  const productIds = packages.map((p) => p.productId);
  logger.debug("[RC] checkIntroEligibility:", productIds);

  const result = await safeCall(
    () => _plugin.checkTrialOrIntroductoryPriceEligibility({ productIdentifiers: productIds }),
    null,
    "checkTrialOrIntroductoryPriceEligibility"
  );

  if (!result.ok || !result.value) {
    logger.debug("[RC] checkIntroEligibility: call failed, defaulting to 'unknown'");
    return packages;
  }

  try {
    const eligibilityMap = result.value;

    for (const pkg of packages) {
      const entry = eligibilityMap[pkg.productId];
      if (!entry) {
        pkg.introEligibility = "unknown";
      } else {
        const status = entry.status ?? entry;
        if (status === 2 || status === "INTRO_ELIGIBILITY_STATUS_ELIGIBLE") {
          pkg.introEligibility = "eligible";
        } else if (status === 1 || status === "INTRO_ELIGIBILITY_STATUS_INELIGIBLE") {
          pkg.introEligibility = "ineligible";
        } else {
          pkg.introEligibility = "unknown";
        }
      }

      logger.debug(`[RC] ${pkg.productId}: eligibility=${pkg.introEligibility}, introOffer=${!!pkg.introOffer}`);
    }
  } catch (e: any) {
    logger.error("[RC] checkIntroEligibility parse error:", e?.message);
  }

  return packages;
}


export async function purchasePackage(
  pkg: RCPackage
): Promise<{ success: boolean; isActive: boolean; error?: string }> {
  await ensurePlugin();
  if (!_plugin || !_initialized) {
    logger.error("[RC] purchasePackage: NOT initialized");
    return { success: false, isActive: false, error: "RevenueCat not initialized" };
  }

  logger.debug("[RC] purchasePackage:", pkg.productId);

  const result = await safeCall(
    () => _plugin.purchasePackage({ aPackage: pkg._raw }),
    null,
    "purchasePackage"
  );

  // User cancelled the native purchase sheet
  if (result.cancelled) {
    logger.debug("[RC] purchasePackage: user cancelled");
    return { success: false, isActive: false, error: "cancelled" };
  }

  if (!result.ok || !result.value) {
    logger.debug("[RC] purchasePackage: native call failed, checking entitlement…");
    const fallbackInfo = await getCustomerInfoSafe();
    logCustomerInfoDeep("purchasePackage-fallback", fallbackInfo);
    const fallbackActive = hasActiveEntitlementFromInfo(fallbackInfo);
    if (fallbackActive) {
      logger.debug("[RC] purchasePackage: fallback confirms active entitlement");
      syncToBackend().catch(() => {});
      return { success: true, isActive: true };
    }
    // FAIL-CLOSED: Purchase call failed AND no verified entitlement — do NOT grant access
    logger.warn("[RC] purchasePackage: no verified entitlement after failed purchase — DENYING access");
    return { success: false, isActive: false, error: "Purchase could not be verified" };
  }

  // Purchase call returned successfully
  const customerInfo = result.value?.customerInfo ?? result.value;
  logCustomerInfoDeep("purchasePackage-success", customerInfo);
  const entitlementActive = hasActiveEntitlementFromInfo(customerInfo);
  logger.debug("[RC] purchasePackage: entitlement active:", entitlementActive);

  syncToBackend().catch(() => {});
  return { success: true, isActive: true };
}

// ── Restore ────────────────────────────────────────────────
export async function restorePurchases(): Promise<{
  success: boolean;
  isActive: boolean;
  error?: string;
}> {
  await ensurePlugin();
  if (!_plugin || !_initialized) {
    logger.error("[RC] restorePurchases: NOT initialized");
    return { success: false, isActive: false, error: "RevenueCat not initialized" };
  }

  logger.debug("[RC] restorePurchases: starting…");

  const result = await safeCall(
    () => _plugin.restorePurchases(),
    null,
    "restorePurchases"
  );

  if (!result.ok || !result.value) {
    logger.debug("[RC] restorePurchases: native call failed, checking fallback…");
    const fallbackInfo = await getCustomerInfoSafe();
    logCustomerInfoDeep("restorePurchases-fallback", fallbackInfo);
    const fallbackActive = hasActiveEntitlementFromInfo(fallbackInfo);
    if (fallbackActive) {
      syncToBackend().catch(() => {});
      return { success: true, isActive: true };
    }
    return { success: false, isActive: false, error: "Restore call failed" };
  }

  const customerInfo = result.value?.customerInfo ?? result.value;
  logCustomerInfoDeep("restorePurchases-success", customerInfo);
  const isActive = hasActiveEntitlementFromInfo(customerInfo);
  logger.debug("[RC] restorePurchases: active:", isActive);
  if (isActive) syncToBackend().catch(() => {});
  return { success: true, isActive };
}

// ── Listener ───────────────────────────────────────────────
type StatusCallback = (isActive: boolean) => void;

export async function addCustomerInfoListener(
  callback: StatusCallback
): Promise<(() => void) | null> {
  await ensurePlugin();
  if (!_plugin || !_initialized) return null;

  const result = await safeCall(
    () =>
      _plugin.addCustomerInfoUpdateListener(({ customerInfo }: any) => {
        try {
          const isActive = hasActiveEntitlementFromInfo(customerInfo);
          callback(isActive);
        } catch (cbErr) {
          logger.warn("[RC] Listener callback error:", cbErr);
        }
      }),
    null,
    "addCustomerInfoUpdateListener"
  );

  if (!result.ok || !result.value) return null;
  const listenerId = result.value;
  return () => {
    try {
      if (listenerId && _plugin?.removeCustomerInfoUpdateListener) {
        _plugin.removeCustomerInfoUpdateListener({ listenerToRemove: listenerId }).catch(() => {});
      }
    } catch {}
  };
}

// ── Backend sync ───────────────────────────────────────────
async function syncToBackend(): Promise<void> {
  try {
    const status = await getSubscriptionStatus();
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user || !status.productId) return;

    await supabase.functions.invoke("sync-apple-subscription", {
      body: {
        product_id: status.productId,
        is_active: status.isActive,
        expiration_date: status.expirationDate,
        is_sandbox: status.isSandbox,
        will_renew: status.willRenew,
        period_type: status.periodType,
      },
    });
    logger.debug("[RC] Backend sync done");
  } catch (e) {
    logger.error("[RC] Backend sync error:", e);
  }
}
